import translate from '@translate';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import Loading from 'src/components/Loading/intex';

import { useCheckPermission } from 'src/utils/checkPermission';
import TopBlock from './TopBlock';
import getBackUrl from 'src/utils/getBackUrl';
import FileUpload, { CustomFileActions, FilesPropType } from 'src/components/FileUpload';
import Typography from 'src/components/Typography';
import downloadFile from 'src/utils/downloadFile';
import { FileImportType } from 'src/types/ordersImport';
import { FileType } from 'src/types/file';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import { WarningIcon } from 'src/components/SkladonIcon/SmallIcons';
import { styled } from '@mui/system';
import { colors } from 'src/themes/default';
import Tooltip from 'src/components/Tooltip';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import ValidationTable from './ValidationTable';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import DownloadIcon from '@mui/icons-material/Download';
import { addFile, deleteFile, importFilesFetch, clearOrdersImport } from 'src/resources/ordersImport/reducer';
import { checkProductsCounts } from './functions';
import NotAllocatedTable from 'src/components/NotAllocatedTable';
import CarriersTable from './CarriersTable';
import { getZasilkovnaCarriersFetch } from 'src/resources/zasilkovna/reducer';
import InfoIcon from '@mui/icons-material/Info';
import ColumnOptions from './ColumnOptions';

const WarningButton = styled(IconButton)<IconButtonProps>({
    '& svg path': {
        fill: colors.primary_O,
    },
});

const findFile = (files: FileImportType[], inputFile: FileType) => {
    return files.find((file) => file.file.name === inputFile.name);
};

const OrdersImportContainer: FC = () => {
    const dispatch = useDispatch();
    const formRef = useRef<HTMLFormElement>(null);

    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [loadingAllocated, setLoadingAllocated] = useState<boolean>(false);
    const [showCarriers, setShowCarriers] = useState<boolean>(false);
    const [showColumnOptions, setShowColumnOptions] = useState<boolean>(false);
    const [orderType, setOrderType] = useState<string>('b2c');

    const loading = useSelector((state: RootStoreType) => state.ordersImport.loading);
    const files = useSelector((state: RootStoreType) => state.ordersImport.files);
    const locationHistory = useSelector((state: RootStoreType) => state.location.history);
    const orderTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.orderTypeCodes);
    const expeditionMethodCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.expeditionMethodCodes
    );
    const dataCarriers = useSelector((state: RootStoreType) => state.zasilkovna.dataCarriers);
    const loadingCarriers = useSelector((state: RootStoreType) => state.zasilkovna.loading);

    const permissionCreate = useCheckPermission('ORDER.CREATE');
    const permissionUpdate = useCheckPermission('ORDER.UPDATE');
    const disabled = !permissionCreate || !permissionUpdate;

    const disableEdit = disabled || !files.filter((file) => !file.imported).length;

    const backUrl = getBackUrl(locationHistory, '/orders/all/');

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('ordersImport.title'), backUrl }));
    }, [backUrl, locationHistory]);

    useEffect(() => {
        switch (locationHistory[1]?.pathname) {
            case '/orders/b2b':
                setOrderType('b2b');
                break;
            case '/orders/b2c':
            default:
                setOrderType('b2c');
                break;
        }
    }, [locationHistory]);

    useEffect(() => {
        return () => {
            dispatch(clearOrdersImport());
        };
    }, []);

    let filesLoading = false;
    let filesError = false;

    const inputFiles = files.map((file) => {
        if (file.loading) {
            filesLoading = true;
        }
        if (file.validation?.orders.find((order) => order.validationErrors?.length)) {
            filesError = true;
        }
        if (file.error) {
            filesError = true;
        }
        return file.file;
    });

    const customFileActions: CustomFileActions = (inputFile) => {
        const file = findFile(files, inputFile);
        if (!file) {
            return null;
        }

        const validationErrors = file.validation?.orders.find((order) => order.validationErrors?.length);

        return (
            <>
                {file.loading && (
                    <IconButton disabled>
                        <CircularProgress color="secondary" size={20} />
                    </IconButton>
                )}
                {validationErrors && (
                    <Tooltip title={translate('ordersImport.incorrectFileData')}>
                        <WarningButton>
                            <WarningIcon />
                        </WarningButton>
                    </Tooltip>
                )}
                {file.error && (
                    <Tooltip title={file.error}>
                        <WarningButton>
                            <WarningIcon />
                        </WarningButton>
                    </Tooltip>
                )}
                {!validationErrors && !file.error && (
                    <IconButton disabled>
                        <TaskAltIcon style={{ fill: 'green' }} />
                    </IconButton>
                )}
            </>
        );
    };

    const dowlaodExcelPattern = () => {
        downloadFile(`/xlsx/import_orders.xlsx`, 'importOrders.xlsx');
    };

    const onUpdate = (files: FilesPropType) => {
        files.map((file) => loadFileData(file));
    };

    const onDelete = (file: FileType) => {
        dispatch(deleteFile(file));
    };

    const handleImport = async () => {
        setLoadingAllocated(true);
        try {
            const { productsCounts, diff } = await checkProductsCounts(files);

            if (!Object.keys(diff).length) {
                dispatch(importFilesFetch());
            } else {
                setConfirmParams({
                    title: translate('ordersImport.confirmNotAviableCount'),
                    content: <NotAllocatedTable products={productsCounts} diff={diff} />,
                    maxWidth: 'md',
                    action: () => {
                        dispatch(importFilesFetch());
                    },
                });
            }
        } finally {
            setLoadingAllocated(false);
        }
    };

    // any becosue of reader.readAsBinaryString(file)
    const loadFileData = (file: any) => {
        const reader = new FileReader();
        reader.readAsBinaryString(file);
        reader.onload = () => {
            dispatch(
                addFile({ file: { data: reader.result, name: file.name, size: file.size, type: file.type }, orderType })
            );
        };
    };

    const handleGetZasilkovnaCarriersData = () => {
        dispatch(getZasilkovnaCarriersFetch(null));
    };

    return (
        <>
            <TopBlock
                disabled={disabled}
                disableEdit={disableEdit}
                filesError={filesError}
                handleImport={handleImport}
                handleCarriers={() => setShowCarriers(true)}
                orderType={orderType}
                setOrderType={setOrderType}
                disableTabs={!!files.length}
            />
            <form ref={formRef}>
                {(loading || loadingAllocated || filesLoading) && <Loading />}
                <Typography component="div" gutterBottom>
                    <span onClick={dowlaodExcelPattern} style={{ cursor: 'pointer' }}>
                        {translate('ordersImport.excelFilePattern')}
                        <IconButton color="primary">
                            <DownloadIcon />
                        </IconButton>
                    </span>
                    <Tooltip title={translate('ordersImport.columnsOptions')}>
                        <IconButton onClick={() => setShowColumnOptions((value) => !value)}>
                            <InfoIcon />
                        </IconButton>
                    </Tooltip>
                </Typography>
                <FileUpload
                    files={inputFiles}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    accept=".xls, .xlsx"
                    height={150}
                    multiple
                    disabled={disabled}
                    rawFiles
                    displayFilesList
                    disableShowFile
                    disableDowloadFile
                    customFileActions={customFileActions}
                />
                <ValidationTable
                    orderTypeCodes={orderTypeCodes}
                    expeditionMethodCodes={expeditionMethodCodes}
                    files={files}
                />

                {confirmParams && (
                    <ConfirmDialog
                        title={confirmParams.title}
                        content={confirmParams.content}
                        action={confirmParams.action}
                        maxWidth={confirmParams.maxWidth}
                        onClose={() => setConfirmParams(null)}
                    />
                )}

                {showCarriers && (
                    <CarriersTable
                        onClose={() => setShowCarriers(false)}
                        getCarriers={handleGetZasilkovnaCarriersData}
                        dataCarriers={dataCarriers}
                        loading={loadingCarriers}
                    />
                )}

                {showColumnOptions && (
                    <ColumnOptions onClose={() => setShowColumnOptions(false)} />
                )}
            </form>
        </>
    );
};

export default OrdersImportContainer;
